if (!customElements.get('pause-product-description')) {
  customElements.define('pause-product-description', class extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.enhance(), { once: true });
      } else {
        this.enhance();
      }
    }

    enhance() {
      const source = this.querySelector('[data-description-source]');
      if (!source || this.dataset.enhanced) return;
      this.toggles = new Map();
      const headings = Array.from(source.querySelectorAll('h1, h2, h3'))
        .filter(heading => heading.textContent.trim() && !heading.closest('table, details, li'));
      const first = headings.findIndex(heading => heading.tagName === 'H3');
      const boundaries = first < 0 ? [] : headings.slice(first);
      const output = document.createDocumentFragment();
      const intro = document.createElement('div');
      intro.className = 'pause-pdp__intro';
      const range = document.createRange();
      range.selectNodeContents(source);
      if (boundaries.length) range.setEndBefore(boundaries[0]);
      intro.append(range.cloneContents());
      this.removeEmptySpacing(intro);
      this.foldIntro(intro);
      output.append(intro);

      boundaries.forEach((heading, index) => {
        const sectionRange = document.createRange();
        sectionRange.selectNodeContents(source);
        sectionRange.setStartBefore(heading);
        if (boundaries[index + 1]) sectionRange.setEndBefore(boundaries[index + 1]);
        if (heading.tagName !== 'H3') {
          output.append(sectionRange.cloneContents());
          return;
        }
        sectionRange.setStartAfter(heading);
        const panel = document.createElement('div');
        panel.className = 'pause-pdp__panel';
        panel.id = `${this.id}-panel-${index + 1}`;
        panel.append(sectionRange.cloneContents());
        this.removeEmptySpacing(panel);
        const title = heading.cloneNode(true);
        title.classList.add('pause-pdp__heading');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pause-pdp__toggle';
        const text = document.createElement('span');
        text.append(...Array.from(title.childNodes));
        const state = document.createElement('span');
        state.className = 'pause-pdp__state';
        state.setAttribute('aria-hidden', 'true');
        button.append(text, state);
        title.append(button);
        const section = document.createElement('div');
        section.className = 'pause-pdp__accordion';
        section.append(title, panel);
        this.connectToggle(button, panel, state, this.dataset.show, this.dataset.hide);
        const headingText = heading.textContent.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        if (window.matchMedia('(min-width: 750px)').matches && /caracteristiques? techniques?|technical (?:specifications?|characteristics?|details)|caracteristicas? tecnicas?/.test(headingText)) {
          panel.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          state.textContent = this.dataset.hide;
        }
        output.append(section);
      });

      source.replaceChildren(output);
      this.dataset.enhanced = 'true';
    }

    removeEmptySpacing(root) {
      Array.from(root.querySelectorAll('p, div')).reverse().forEach(node => {
        if (!node.textContent.replace(/[\s\u200e\u200f\u200b]/g, '') &&
            !node.querySelector('img, video, iframe, audio, svg, input, button, hr') && !node.id) {
          node.remove();
        }
      });
    }

    foldIntro(intro) {
      const firstParagraph = Array.from(intro.querySelectorAll('p'))
        .find(paragraph => paragraph.textContent.trim());
      if (!firstParagraph) return;
      const range = document.createRange();
      range.selectNodeContents(intro);
      range.setStartBefore(firstParagraph);
      const fullText = range.toString().replace(/\s+/g, ' ').trim();
      if (Array.from(fullText).length <= 240) return;

      const panel = document.createElement('div');
      panel.id = `${this.id}-more`;
      panel.className = 'pause-pdp__intro-more';
      panel.append(range.extractContents());
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pause-pdp__read-more';
      panel.hidden = true;
      button.textContent = this.dataset.more;
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', panel.id);
      const preview = document.createElement('p');
      preview.className = 'pause-pdp__preview-paragraph';
      const previewText = Array.from(fullText).slice(0, 240).join('');
      preview.textContent = previewText;
      if (this.dataset.preorder === 'true') {
        const opening = firstParagraph.querySelector('strong, b') || firstParagraph;
        const openingText = opening.textContent.replace(/\s+/g, ' ').trim();
        if (fullText.startsWith(openingText)) {
          const bold = document.createElement('strong');
          const boldText = Array.from(openingText).slice(0, 240).join('');
          bold.textContent = boldText;
          preview.replaceChildren(bold, document.createTextNode(previewText.slice(boldText.length)));
        }
      }
      intro.classList.add('pause-pdp__intro--collapsed');
      button.addEventListener('click', () => {
        const expanded = panel.hidden;
        const scrollPosition = window.scrollY;
        panel.hidden = !expanded;
        preview.hidden = expanded;
        intro.classList.toggle('pause-pdp__intro--collapsed', !expanded);
        button.textContent = expanded ? this.dataset.less : this.dataset.more;
        button.setAttribute('aria-expanded', String(expanded));
        if (expanded) {
          window.scrollTo({ top: scrollPosition, behavior: 'instant' });
          requestAnimationFrame(() => window.scrollTo({ top: scrollPosition, behavior: 'instant' }));
        } else {
          const bounds = intro.getBoundingClientRect();
          if (bounds.top < 90) intro.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      });
      intro.append(preview, panel, button);
    }

    connectToggle(button, panel, label, closedText, openText) {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', panel.id);
      label.textContent = closedText;
      this.toggles.set(button, { panel, label, closedText });
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        if (expanded) {
          this.toggles.forEach((other, control) => {
            if (control === button) return;
            other.panel.hidden = true;
            control.setAttribute('aria-expanded', 'false');
            other.label.textContent = other.closedText;
          });
        }
        panel.hidden = !expanded;
        button.setAttribute('aria-expanded', String(expanded));
        label.textContent = expanded ? openText : closedText;
        if (expanded && !button.classList.contains('pause-pdp__read-more')) {
          requestAnimationFrame(() => {
            const bounds = button.getBoundingClientRect();
            if (bounds.top < 90 || bounds.bottom > window.innerHeight) {
              button.scrollIntoView({ block: 'start', behavior: 'instant' });
            }
          });
        }
      });
    }
  });
}


